import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiCpu, FiPlus, FiTrash2, FiShield, FiCheckCircle,
  FiExternalLink, FiUsers, FiUser, FiRefreshCw, FiSearch, FiArrowRight,
} from 'react-icons/fi';
import { FaWhatsapp, FaYoutube } from 'react-icons/fa';
import { automationSourceService, whatsAppConfigService, youtubeConfigService, youtubeAccountService, mappingService } from '../../services/apiService';
import { useAuthStore } from '../../utils/store';
import { useT } from '../../utils/i18n';
import { canPoll } from '../../utils/requestGovernor';
import { SearchableSelect } from '../../components/Common/SearchableSelect';

const emptyForm = { key: '', name: '', type: 'input' };

// A small numbered badge tying each of the three tables below together into
// one "wire it up in order" mental model: sources -> destinations -> defaults.
const StepBadge = ({ n }) => (
  <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">
    {n}
  </span>
);

export const AutomationSources = () => {
  const t = useT();
  const { user } = useAuthStore();
  // Managers get this one page — configuring pipeline input/output sources —
  // without any other admin privilege.
  const canManage = user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'manager';

  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState(emptyForm);
  const [saving, setSaving]   = useState(false);
  const [showCustomForm, setShowCustomForm] = useState(false);

  useEffect(() => {
    if (!canManage) { setLoading(false); return; }
    fetchSources();
  }, [canManage]);

  const fetchSources = async () => {
    setLoading(true);
    try {
      const data = await automationSourceService.listSources();
      setSources(data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (source) => {
    try {
      const updated = await automationSourceService.updateSource(source._id, { enabled: !source.enabled });
      setSources((prev) => prev.map((s) => (s._id === source._id ? updated : s)));
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  const handleDelete = async (source) => {
    if (source.key === 'whatsapp') return; // the built-in WhatsApp source can be disconnected, not removed
    if (!window.confirm(t('automationSources.deleteConfirm'))) return;
    try {
      await automationSourceService.deleteSource(source._id);
      setSources((prev) => prev.filter((s) => s._id !== source._id));
      toast.success(t('automationSources.deleted'));
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.key.trim() || !form.name.trim()) return;
    setSaving(true);
    try {
      const created = await automationSourceService.createSource({
        key: form.key.trim().toLowerCase(),
        name: form.name.trim(),
        type: form.type,
      });
      setSources((prev) => [...prev, created]);
      setForm(emptyForm);
      toast.success(t('automationSources.added'));
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-400 gap-3">
        <FiShield size={40} className="text-gray-300" />
        <p className="text-sm">{t('settings.adminOnly')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
        {t('settings.loadingPrompts')}
      </div>
    );
  }

  const whatsappSource = sources.find((s) => s.key === 'whatsapp');
  const youtubeSources = sources.filter((s) => s.provider === 'youtube');
  const otherSources = sources.filter((s) => s.key !== 'whatsapp' && s.provider !== 'youtube');
  const otherInput  = otherSources.filter((s) => s.type === 'input');
  const otherOutput = otherSources.filter((s) => s.type === 'output');
  const outputSources = sources.filter((s) => s.type === 'output');

  const SourceGroup = ({ label, list }) => (
    <div className="card p-0 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label} ({list.length})</p>
      </div>
      {list.length === 0 ? (
        <p className="px-4 py-6 text-sm text-gray-400 text-center">{t('automationSources.noSources')}</p>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {list.map((s) => (
              <tr key={s._id} className="border-b border-gray-50 last:border-0">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-800">{s.name}</p>
                  <p className="text-xs text-gray-400 font-mono">{s.key}</p>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => handleToggle(s)}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 align-middle ${
                      s.enabled ? 'bg-primary-500' : 'bg-gray-300'
                    }`}
                    role="switch"
                    aria-checked={s.enabled}
                    title={t('automationSources.enabled')}
                  >
                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform duration-200 ${s.enabled ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => handleDelete(s)} title={t('ipWhitelist.remove')} className="text-gray-400 hover:text-danger-600 transition">
                    <FiTrash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div className="space-y-5 max-w-6xl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
          <FiCpu size={20} className="text-primary-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('settings.automationSources')}</h1>
          <p className="text-sm text-gray-500">{t('settings.automationSourcesSubtitle')}</p>
        </div>
      </div>

      {/* The three-table flow this page is built around: what comes IN, where
          it can go OUT, and which input defaults to which output. Each
          section's own header carries a step badge (1/2/3) to keep that
          order visible as you scroll. */}
      <WhatsAppConnectionBar t={t} onSaved={fetchSources} />

      <InputSourcesTable t={t} whatsappSource={whatsappSource} onSaved={fetchSources} />

      <OutputDestinationsSection t={t} youtubeSources={youtubeSources} onChanged={fetchSources} />

      <DefaultMappingsPanel
        t={t}
        whatsappSource={whatsappSource}
        outputSources={outputSources}
      />

      {(otherInput.length > 0 || otherOutput.length > 0 || showCustomForm) && (
        <div className="pt-2 border-t border-gray-100 space-y-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{t('automationSources.otherSources')}</p>
          {otherInput.length > 0 && <SourceGroup label={t('automationSources.input')} list={otherInput} />}
          {otherOutput.length > 0 && <SourceGroup label={t('automationSources.output')} list={otherOutput} />}
        </div>
      )}

      {showCustomForm ? (
        <form onSubmit={handleAdd} className="card p-4 space-y-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('automationSources.addSource')}</p>
          <div className="flex gap-2 flex-wrap sm:flex-nowrap">
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder={t('automationSources.namePlaceholder')}
              className="input text-sm flex-1 min-w-0"
            />
            <input
              value={form.key}
              onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
              placeholder={t('automationSources.keyPlaceholder')}
              className="input text-sm font-mono sm:w-40"
            />
            <select
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              className="input text-sm sm:w-36"
            >
              <option value="input">{t('automationSources.input')}</option>
              <option value="output">{t('automationSources.output')}</option>
            </select>
            <button type="submit" disabled={saving} className="btn btn-primary flex items-center gap-1.5 text-sm flex-shrink-0">
              <FiPlus size={14} />
              {t('automationSources.addSource')}
            </button>
          </div>
        </form>
      ) : (
        <button
          onClick={() => setShowCustomForm(true)}
          className="text-xs text-gray-400 hover:text-gray-600 transition"
        >
          {t('automationSources.addCustomSource')}
        </button>
      )}
    </div>
  );
};

// ─── WhatsApp connection status bar ───────────────────────────────────────────
// Purely the live session (QR/connect/disconnect/sync) — the monitored groups
// and contacts that make up Table 1 are config data, shown/edited in
// InputSourcesTable below regardless of whether the live session is currently
// connected, so an admin can pre-configure the allow-list before ever scanning
// a QR code, or review it while briefly disconnected.

const WHATSAPP_INITIALIZING_POLL_MS = 10000;
const WHATSAPP_QR_POLL_MS = 5000;
const WHATSAPP_HEARTBEAT_MS = 180000;
const WHATSAPP_MAX_TRANSIENT_POLLS = 18; // ~a few minutes total before giving up
const WHATSAPP_DISCONNECTED_POLL_MS = 15000;
const WHATSAPP_MAX_DISCONNECTED_POLLS = 40; // ~10 minutes before giving up

const WhatsAppConnectionBar = ({ t, onSaved }) => {
  const [status, setStatus] = useState({ status: 'disconnected', qrDataUrl: null, phoneNumber: null });
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [stalled, setStalled] = useState(false);
  const disconnectedPollCountRef = useRef(0);
  const transientPollCountRef = useRef(0);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await whatsAppConfigService.getStatus();
      setStatus(data);
      if (data.status !== 'initializing' && data.status !== 'qr_pending') {
        transientPollCountRef.current = 0;
        setStalled(false);
      }
      return data;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Fast/slow polling while connecting, bounded by WHATSAPP_MAX_TRANSIENT_POLLS.
  useEffect(() => {
    const transient = status.status === 'initializing' || status.status === 'qr_pending';
    if (!transient || stalled) return undefined;

    const intervalMs = status.status === 'qr_pending' ? WHATSAPP_QR_POLL_MS : WHATSAPP_INITIALIZING_POLL_MS;
    const id = setInterval(() => {
      transientPollCountRef.current += 1;
      if (transientPollCountRef.current >= WHATSAPP_MAX_TRANSIENT_POLLS) {
        setStalled(true);
        return;
      }
      if (!canPoll()) return; // client-side budget tight — skip, retry next tick
      fetchStatus();
    }, intervalMs);
    return () => clearInterval(id);
  }, [status.status, stalled, fetchStatus]);

  // Infrequent heartbeat once connected, just to notice an unexpected drop.
  useEffect(() => {
    if (status.status !== 'connected') return undefined;
    const id = setInterval(() => {
      if (!canPoll()) return;
      fetchStatus();
    }, WHATSAPP_HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [status.status, fetchStatus]);

  // Also poll (moderately) while disconnected. This page previously fetched
  // status exactly once on mount and then never again in the disconnected
  // state — meaning a reconnect triggered from elsewhere (the Pipeline page's
  // refresh button now pokes the connection when it's down; the backend's own
  // watchdog can also self-heal a stuck session on its own) would never show
  // up here without a manual reload, even though the backend had already
  // recovered. Capped so a WhatsApp integration nobody has ever connected
  // doesn't poll forever.
  useEffect(() => {
    if (status.status !== 'disconnected') {
      disconnectedPollCountRef.current = 0;
      return undefined;
    }
    const id = setInterval(() => {
      disconnectedPollCountRef.current += 1;
      if (disconnectedPollCountRef.current >= WHATSAPP_MAX_DISCONNECTED_POLLS) {
        clearInterval(id);
        return;
      }
      if (!canPoll()) return;
      fetchStatus();
    }, WHATSAPP_DISCONNECTED_POLL_MS);
    return () => clearInterval(id);
  }, [status.status, fetchStatus]);

  const handleManualCheck = () => {
    transientPollCountRef.current = 0;
    setStalled(false);
    fetchStatus();
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const data = await whatsAppConfigService.connect();
      setStatus(data);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await whatsAppConfigService.disconnect();
      setStatus({ status: 'disconnected', qrDataUrl: null, phoneNumber: null });
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      await whatsAppConfigService.sync();
      toast.success(t('automationSources.whatsapp.syncDone'));
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <FaWhatsapp size={20} className="text-[#25D366] flex-shrink-0" />
          <div>
            <p className="font-semibold text-gray-800">{t('automationSources.whatsapp.title')}</p>
            <p className="text-xs text-gray-500">{t('automationSources.whatsapp.subtitle')}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {status.status === 'connected' ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success-700 bg-success-100 px-2.5 py-1 rounded-full">
              <FiCheckCircle size={12} />
              {t('automationSources.connected')}{status.phoneNumber ? ` · +${status.phoneNumber}` : ''}
            </span>
          ) : (
            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
              {t(`automationSources.whatsapp.status.${status.status}`)}
            </span>
          )}
          {status.status === 'connected' && (
            <>
              <button onClick={handleSyncNow} disabled={syncing} className="btn btn-ghost btn-sm text-xs flex items-center gap-1.5">
                <FiRefreshCw size={12} className={syncing ? 'animate-spin' : ''} />
                {syncing ? t('automationSources.whatsapp.syncing') : t('automationSources.whatsapp.syncNow')}
              </button>
              <button onClick={handleDisconnect} className="btn btn-ghost btn-sm text-xs text-danger-600">
                {t('automationSources.whatsapp.disconnect')}
              </button>
            </>
          )}
        </div>
      </div>

      {status.status === 'disconnected' && (
        <button onClick={handleConnect} disabled={connecting} className="btn btn-primary btn-sm flex items-center gap-1.5">
          <FaWhatsapp size={13} />
          {connecting ? t('automationSources.whatsapp.connecting') : t('automationSources.whatsapp.connect')}
        </button>
      )}

      {(status.status === 'initializing') && !stalled && (
        <p className="text-xs text-gray-400">{t('automationSources.whatsapp.initializing')}</p>
      )}

      {status.status === 'qr_pending' && status.qrDataUrl && !stalled && (
        <div className="flex flex-col items-center gap-2 py-2">
          <img src={status.qrDataUrl} alt="WhatsApp QR code" className="w-48 h-48 border border-gray-200 rounded-lg" />
          <p className="text-xs text-gray-500">{t('automationSources.whatsapp.scanHint')}</p>
        </div>
      )}

      {stalled && (
        <div className="flex flex-col items-center gap-2 py-2 text-center">
          <p className="text-xs text-gray-500">{t('automationSources.whatsapp.stalledHint')}</p>
          <button onClick={handleManualCheck} className="btn btn-ghost btn-sm flex items-center gap-1.5 text-xs">
            <FiRefreshCw size={12} />
            {t('automationSources.whatsapp.checkAgain')}
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Table 1: Input Sources (monitored WhatsApp groups/contacts) ─────────────
// Each add/remove persists immediately (no separate "save" step) — matches
// how every other action on this page already behaves, and removes a step
// that only existed here before.

const InputSourcesTable = ({ t, whatsappSource, onSaved }) => {
  const [groupNameInput, setGroupNameInput] = useState('');
  const [groupLabelInput, setGroupLabelInput] = useState('');
  const [contactNumberInput, setContactNumberInput] = useState('');
  const [contactLabelInput, setContactLabelInput] = useState('');
  const [saving, setSaving] = useState(false);

  const propMonitored = whatsappSource?.config?.monitored || { contacts: [], groups: [] };
  // Optimistic local copy, not a direct read of the prop — every add/remove
  // computes its next state from THIS, not from `whatsappSource`. Each save
  // still round-trips through the parent's onSaved -> fetchSources -> new
  // prop, but that round trip is not instant, and the Save button re-enables
  // (saving -> false) the moment our OWN request resolves, before that
  // refresh has necessarily landed. Deriving the next state from the prop in
  // that window would silently drop whatever was just added: a fast second
  // add starts from a monitored list that doesn't contain the first add yet,
  // and its wholesale-replace save overwrites the server with that stale,
  // incomplete list. Deriving from local state instead means each click
  // always builds on the previous one, in order, regardless of how fast the
  // network round trip actually is.
  const [monitored, setMonitored] = useState(propMonitored);
  useEffect(() => {
    setMonitored(propMonitored);
    // eslint-disable-next-line
  }, [JSON.stringify(propMonitored)]);

  const rows = useMemo(() => [
    ...(monitored.groups || []).map((g) => ({ type: 'group', id: `group:${g.name}`, value: g.name, label: g.label })),
    ...(monitored.contacts || []).map((c) => ({ type: 'contact', id: `contact:${c.number}`, value: c.number, label: c.label })),
  ], [monitored]);

  // Saves are serialized (the Add/Remove controls are disabled while `saving`
  // is true) precisely so only one request is ever in flight — two concurrent
  // wholesale-replace requests could still land out of order and have the
  // same lost-update problem even with correct local state. One at a time,
  // always built on the latest known-good state, closes it completely.
  const persist = async (nextMonitored) => {
    setSaving(true);
    setMonitored(nextMonitored);
    try {
      await whatsAppConfigService.updateMonitored(nextMonitored);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
      setMonitored(propMonitored); // roll back the optimistic update on failure
    } finally {
      setSaving(false);
    }
  };

  const handleAddGroup = (e) => {
    e.preventDefault();
    const name = groupNameInput.trim();
    if (!name) return;
    if ((monitored.groups || []).some((g) => g.name.toLowerCase() === name.toLowerCase())) {
      toast.info(t('automationSources.whatsapp.alreadyAdded'));
      return;
    }
    persist({ groups: [...(monitored.groups || []), { name, label: groupLabelInput.trim() }], contacts: monitored.contacts || [] });
    setGroupNameInput('');
    setGroupLabelInput('');
  };

  const handleAddContact = (e) => {
    e.preventDefault();
    const number = contactNumberInput.replace(/\D/g, '');
    if (!number || number.length < 8) {
      toast.error(t('automationSources.whatsapp.invalidNumber'));
      return;
    }
    if ((monitored.contacts || []).some((c) => c.number === number)) {
      toast.info(t('automationSources.whatsapp.alreadyAdded'));
      return;
    }
    persist({ groups: monitored.groups || [], contacts: [...(monitored.contacts || []), { number, label: contactLabelInput.trim() }] });
    setContactNumberInput('');
    setContactLabelInput('');
  };

  const handleRemove = (row) => {
    if (row.type === 'group') {
      persist({ groups: (monitored.groups || []).filter((g) => g.name !== row.value), contacts: monitored.contacts || [] });
    } else {
      persist({ groups: monitored.groups || [], contacts: (monitored.contacts || []).filter((c) => c.number !== row.value) });
    }
  };

  return (
    <div className="card p-0">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 rounded-t-xl">
        <p className="font-semibold text-gray-800 flex items-center gap-2">
          <StepBadge n={1} />
          {t('automationSources.inputs.title')} ({rows.length})
        </p>
        <p className="text-xs text-gray-500 mt-0.5">{t('automationSources.inputs.subtitle')}</p>
      </div>

      {rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-gray-400 text-center">{t('automationSources.inputs.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-gray-100">
                <th className="px-4 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('automationSources.inputs.colType')}</th>
                <th className="px-4 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('automationSources.inputs.colName')}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-gray-50 last:border-0">
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 whitespace-nowrap">
                      {row.type === 'group' ? <FiUsers size={12} /> : <FiUser size={12} />}
                      {row.type === 'group' ? t('automationSources.whatsapp.groups') : t('automationSources.whatsapp.contacts')}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <p className="text-gray-800">{row.label || (row.type === 'contact' ? `+${row.value}` : row.value)}</p>
                    {row.label && <p className="text-xs text-gray-400 font-mono">{row.type === 'contact' ? `+${row.value}` : row.value}</p>}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => handleRemove(row)} disabled={saving} title={t('ipWhitelist.remove')} className="text-gray-400 hover:text-danger-600 transition">
                      <FiTrash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="border-t border-gray-100 p-4 grid sm:grid-cols-2 gap-4">
        <form onSubmit={handleAddGroup} className="space-y-1.5">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
            <FiUsers size={12} className="text-gray-400" /> {t('automationSources.whatsapp.groups')}
          </p>
          <div className="flex gap-2">
            <input
              value={groupNameInput}
              onChange={(e) => setGroupNameInput(e.target.value)}
              placeholder={t('automationSources.whatsapp.groupNamePlaceholder')}
              className="input text-sm flex-1 min-w-0"
            />
            <input
              value={groupLabelInput}
              onChange={(e) => setGroupLabelInput(e.target.value)}
              placeholder={t('automationSources.whatsapp.labelPlaceholder')}
              className="input text-sm w-28 flex-shrink-0"
            />
            <button type="submit" disabled={saving} className="btn btn-ghost btn-sm flex-shrink-0"><FiPlus size={13} /></button>
          </div>
          <p className="text-[11px] text-gray-400">{t('automationSources.whatsapp.groupNameHint')}</p>
        </form>
        <form onSubmit={handleAddContact} className="space-y-1.5">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
            <FiUser size={12} className="text-gray-400" /> {t('automationSources.whatsapp.contacts')}
          </p>
          <div className="flex gap-2">
            <input
              value={contactNumberInput}
              onChange={(e) => setContactNumberInput(e.target.value)}
              placeholder={t('automationSources.whatsapp.contactNumberPlaceholder')}
              className="input text-sm flex-1 min-w-0 font-mono"
            />
            <input
              value={contactLabelInput}
              onChange={(e) => setContactLabelInput(e.target.value)}
              placeholder={t('automationSources.whatsapp.labelPlaceholder')}
              className="input text-sm w-28 flex-shrink-0"
            />
            <button type="submit" disabled={saving} className="btn btn-ghost btn-sm flex-shrink-0"><FiPlus size={13} /></button>
          </div>
          <p className="text-[11px] text-gray-400">{t('automationSources.whatsapp.contactNumberHint')}</p>
        </form>
      </div>
    </div>
  );
};

// ─── Table 2: Output Destinations (YouTube accounts + channels) ──────────────
// Accounts (Google OAuth app credentials) are a small management strip above
// the actual destinations table — a channel can't exist without an account,
// but the account itself is never a publish target, only its channels are.

const OutputDestinationsSection = ({ t, youtubeSources, onChanged }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [addingAccount, setAddingAccount] = useState(false);
  const [editingAccountId, setEditingAccountId] = useState(null);
  const [suggestedUri, setSuggestedUri] = useState('');
  const [connectingAccountId, setConnectingAccountId] = useState(null);

  const fetchAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    try {
      const data = await youtubeAccountService.list();
      setAccounts(data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);
  useEffect(() => {
    youtubeConfigService.getRedirectUri().then((d) => setSuggestedUri(d.redirectUri)).catch(() => {});
  }, []);

  // Handle the ?youtube=connected|error redirect back from Google's OAuth consent screen.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const result = params.get('youtube');
    if (!result) return;
    if (result === 'connected') {
      // Names the actual channel Google resolved to, and stays on screen
      // until dismissed — confirmed live (2026-07-24) that this step can
      // succeed while silently landing on the WRONG channel (a Brand Account
      // consent started without switching to it first can authorize the
      // signed-in personal channel instead, with no error at all). A generic
      // "connected!" toast makes that invisible; naming the channel here is
      // what actually lets an admin catch it before it gets used to publish.
      const channelTitle = params.get('channel');
      toast.success(
        channelTitle ? t('automationSources.youtube.connectedToastNamed', { channel: channelTitle }) : t('automationSources.youtube.connectedToast'),
        { autoClose: false }
      );
      onChanged?.();
      fetchAccounts();
    } else if (result === 'error') {
      // autoClose: false — OAuth failure messages can be a full actionable
      // instruction (e.g. the Brand Account "switch account first" guidance),
      // not a short status update; the app-wide 4s default would hide it
      // before anyone could read it, let alone act on it.
      toast.error(params.get('message') || t('automationSources.youtube.connectFailed'), { autoClose: false });
    }
    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line
  }, [location.search]);

  const accountsById = useMemo(() => {
    const map = {};
    accounts.forEach((a) => { map[a._id] = a; });
    return map;
  }, [accounts]);

  const channelCountByAccount = useMemo(() => {
    const map = {};
    youtubeSources.forEach((s) => {
      const accId = s.config?.accountId || '';
      map[accId] = (map[accId] || 0) + 1;
    });
    return map;
  }, [youtubeSources]);

  const handleConnect = async (accountId) => {
    setConnectingAccountId(accountId);
    try {
      const { url } = await youtubeAccountService.getAuthUrl(accountId);
      window.location.href = url;
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
      setConnectingAccountId(null);
    }
  };

  const handleDeleteAccount = async (account) => {
    if ((channelCountByAccount[account._id] || 0) > 0) return;
    if (!window.confirm(t('automationSources.youtube.deleteAccountConfirm'))) return;
    try {
      await youtubeAccountService.remove(account._id);
      toast.success(t('automationSources.youtube.accountDeleted'));
      fetchAccounts();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  const refreshBoth = () => { onChanged?.(); fetchAccounts(); };

  return (
    <div className="space-y-3">
      {/* Accounts — Google OAuth app credentials, one or more, each with its
          own channels below. */}
      <div className="card p-0">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 rounded-t-xl">
          <p className="font-semibold text-gray-800 flex items-center gap-2">
            <FaYoutube size={16} className="text-[#FF0000]" />
            {t('automationSources.youtube.accountsTitle')}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">{t('automationSources.youtube.subtitle')}</p>
        </div>

        {loadingAccounts ? (
          <p className="px-4 py-6 text-sm text-gray-400 text-center">{t('settings.loadingPrompts')}</p>
        ) : accounts.length === 0 && !addingAccount ? (
          <p className="px-4 py-6 text-sm text-gray-400 text-center">{t('automationSources.youtube.noAccountsYet')}</p>
        ) : accounts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-gray-100">
                  <th className="px-4 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('automationSources.youtube.colAccount')}</th>
                  <th className="px-4 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">{t('automationSources.youtube.clientIdPlaceholder')}</th>
                  <th className="px-4 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('automationSources.youtube.channelsInAccount')}</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  editingAccountId === account._id ? (
                    <tr key={account._id}>
                      <td colSpan={4} className="p-3">
                        <YouTubeAccountForm
                          t={t}
                          account={account}
                          suggestedUri={suggestedUri}
                          onCancel={() => setEditingAccountId(null)}
                          onSaved={() => { setEditingAccountId(null); fetchAccounts(); }}
                        />
                      </td>
                    </tr>
                  ) : (
                    <tr key={account._id} className="border-b border-gray-50 last:border-0">
                      <td className="px-4 py-2.5 text-gray-800 font-medium whitespace-nowrap">{account.name}</td>
                      <td className="px-4 py-2.5 text-gray-400 font-mono text-xs truncate max-w-[220px] hidden sm:table-cell">{account.clientId}</td>
                      <td className="px-4 py-2.5 text-gray-600">{channelCountByAccount[account._id] || 0}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                          <button
                            onClick={() => handleConnect(account._id)}
                            disabled={connectingAccountId === account._id}
                            className="btn btn-secondary btn-sm text-xs flex items-center gap-1 whitespace-nowrap"
                          >
                            <FiExternalLink size={11} />
                            {connectingAccountId === account._id ? t('automationSources.youtube.connecting') : t('automationSources.youtube.connectChannel')}
                          </button>
                          <button onClick={() => setEditingAccountId(account._id)} className="btn btn-ghost btn-sm text-xs">{t('toolbar.edit')}</button>
                          <button
                            onClick={() => handleDeleteAccount(account)}
                            disabled={(channelCountByAccount[account._id] || 0) > 0}
                            title={(channelCountByAccount[account._id] || 0) > 0 ? t('automationSources.youtube.deleteAccountConfirm') : t('ipWhitelist.remove')}
                            className="text-gray-400 hover:text-danger-600 transition disabled:opacity-30 disabled:hover:text-gray-400"
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {accounts.length > 0 && (
          <p className="px-4 pt-3 text-[11px] text-gray-400">{t('automationSources.youtube.multiChannelHint')}</p>
        )}

        <div className="p-3 border-t border-gray-100">
          {addingAccount ? (
            <YouTubeAccountForm
              t={t}
              suggestedUri={suggestedUri}
              onCancel={() => setAddingAccount(false)}
              onSaved={() => { setAddingAccount(false); fetchAccounts(); }}
            />
          ) : (
            <button onClick={() => setAddingAccount(true)} className="btn btn-ghost btn-sm text-xs flex items-center gap-1.5 text-primary-600">
              <FiPlus size={13} /> {t('automationSources.youtube.addAccount')}
            </button>
          )}
        </div>
      </div>

      {/* Channels — the actual publish destinations. */}
      <div className="card p-0">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 rounded-t-xl">
          <p className="font-semibold text-gray-800 flex items-center gap-2">
            <StepBadge n={2} />
            {t('automationSources.outputs.title')} ({youtubeSources.length})
          </p>
          <p className="text-xs text-gray-500 mt-0.5">{t('automationSources.outputs.subtitle')}</p>
        </div>
        {youtubeSources.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-400 text-center">{t('automationSources.youtube.noChannelsYet')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-gray-100">
                  <th className="px-4 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('automationSources.outputs.colChannel')}</th>
                  <th className="px-4 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('automationSources.youtube.colAccount')}</th>
                  <th className="px-4 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('automationSources.outputs.colStatus')}</th>
                  <th className="px-4 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('automationSources.youtube.defaultPrivacyLabel')}</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {youtubeSources.map((source) => (
                  <OutputChannelRow
                    key={source._id}
                    t={t}
                    source={source}
                    accountName={accountsById[source.config?.accountId]?.name}
                    onChanged={refreshBoth}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Add/edit a YouTube account's OAuth credentials ───────────────────────────

const YouTubeAccountForm = ({ t, account, suggestedUri, onCancel, onSaved }) => {
  const [name, setName] = useState(account?.name || '');
  const [clientId, setClientId] = useState(account?.clientId || '');
  const [clientSecret, setClientSecret] = useState('');
  const [redirectUri, setRedirectUri] = useState(account?.redirectUri || suggestedUri || '');
  const [saving, setSaving] = useState(false);

  const handleUseSuggested = () => setRedirectUri(suggestedUri);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !clientId.trim() || !redirectUri.trim()) return;
    if (!account && !clientSecret.trim()) return; // required on create; blank on edit means "keep existing"
    setSaving(true);
    try {
      if (account) {
        await youtubeAccountService.update(account._id, {
          name: name.trim(),
          clientId: clientId.trim(),
          redirectUri: redirectUri.trim(),
          ...(clientSecret.trim() ? { clientSecret: clientSecret.trim() } : {}),
        });
      } else {
        await youtubeAccountService.create({
          name: name.trim(),
          clientId: clientId.trim(),
          clientSecret: clientSecret.trim(),
          redirectUri: redirectUri.trim(),
        });
      }
      toast.success(t('automationSources.youtube.accountSaved'));
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="border border-gray-100 rounded-xl p-3.5 space-y-2.5">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t('automationSources.youtube.accountNamePlaceholder')}
        autoFocus
        className="input text-sm w-full"
      />
      <input
        value={clientId}
        onChange={(e) => setClientId(e.target.value)}
        placeholder={t('automationSources.youtube.clientIdPlaceholder')}
        className="input text-sm w-full"
      />
      <input
        value={clientSecret}
        onChange={(e) => setClientSecret(e.target.value)}
        type="password"
        placeholder={account ? t('automationSources.youtube.clientSecretStoredPlaceholder') : t('automationSources.youtube.clientSecretPlaceholder')}
        className="input text-sm w-full"
      />
      <div>
        <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">{t('automationSources.youtube.redirectUriLabel')}</label>
        <input
          value={redirectUri}
          onChange={(e) => setRedirectUri(e.target.value)}
          placeholder={t('automationSources.youtube.redirectUriPlaceholder')}
          className="input text-sm w-full font-mono mt-1"
        />
        <p className="text-[11px] text-gray-400 mt-1">{t('automationSources.youtube.redirectUriHint')}</p>
        {suggestedUri && suggestedUri !== redirectUri && (
          <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-1 flex-wrap">
            {t('automationSources.youtube.suggestedUri')} <code className="text-gray-600">{suggestedUri}</code>
            <button type="button" onClick={handleUseSuggested} className="text-primary-600 hover:underline font-medium">
              {t('automationSources.youtube.useSuggested')}
            </button>
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button type="submit" disabled={saving} className="btn btn-primary btn-sm text-xs">
          {saving ? t('settings.saving') : t('settings.save')}
        </button>
        <button type="button" onClick={onCancel} className="btn btn-ghost btn-sm text-xs">
          {t('toolbar.close')}
        </button>
      </div>
    </form>
  );
};

// ─── One channel row in the Output Destinations table ─────────────────────────

const OutputChannelRow = ({ t, source, accountName, onChanged }) => {
  const [status, setStatus] = useState({ connected: !!source?.config?.connected, channelTitle: source?.config?.channelTitle || '' });
  const [privacyStatus, setPrivacyStatus] = useState(source?.config?.defaultPrivacyStatus || 'unlisted');
  // One shared flag across disconnect/delete/privacy-change, not one per
  // action — disconnect writes 'config.connected' via a targeted $set, while
  // the privacy save PUTs the whole 'config' object built from this row's own
  // (possibly stale) client-side copy of it. If both were allowed to be in
  // flight at once and the privacy save's response landed after the
  // disconnect's, its stale spread of source.config would silently write
  // connected: true right back, undoing the disconnect. Serializing every
  // mutation on this row behind one flag makes that ordering impossible.
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    youtubeConfigService.getStatus(source.key).then(setStatus).catch(() => {});
  }, [source.key]);

  useEffect(() => {
    if (source?.config?.defaultPrivacyStatus) setPrivacyStatus(source.config.defaultPrivacyStatus);
  }, [source?.config?.defaultPrivacyStatus]);

  const handleDisconnect = async () => {
    setBusy(true);
    try {
      await youtubeConfigService.disconnect(source.key);
      setStatus((s) => ({ ...s, connected: false }));
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('automationSources.deleteConfirm'))) return;
    setBusy(true);
    try {
      await automationSourceService.deleteSource(source._id);
      toast.success(t('automationSources.deleted'));
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  // config is Mixed and saved wholesale server-side (PUT replaces it, not
  // merges it) — spread the existing config so channelId/channelTitle/
  // accountId aren't wiped out by this save. Also folds in the latest known
  // `connected` state from `status` (not just source.config, which can be one
  // render behind it) so this can never write a stale `connected` value back
  // over a disconnect that already landed.
  const handlePrivacyChange = async (e) => {
    const value = e.target.value;
    setPrivacyStatus(value);
    setBusy(true);
    try {
      await automationSourceService.updateSource(source._id, {
        config: { ...source.config, connected: status.connected, defaultPrivacyStatus: value },
      });
      toast.success(t('automationSources.youtube.defaultPrivacySaved'));
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="border-b border-gray-50 last:border-0">
      <td className="px-4 py-2.5 text-gray-800 font-medium whitespace-nowrap">{status.channelTitle || source.name}</td>
      <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{accountName || '—'}</td>
      <td className="px-4 py-2.5">
        {status.connected ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-success-700 whitespace-nowrap">
            <FiCheckCircle size={11} /> {t('automationSources.connected')}
          </span>
        ) : (
          <span className="text-xs text-gray-400 whitespace-nowrap">{t('automationSources.notConnected')}</span>
        )}
      </td>
      <td className="px-4 py-2.5">
        <select value={privacyStatus} onChange={handlePrivacyChange} disabled={busy} className="input text-xs py-1">
          <option value="public">{t('youtube.privacyPublic')}</option>
          <option value="unlisted">{t('youtube.privacyUnlisted')}</option>
          <option value="private">{t('youtube.privacyPrivate')}</option>
        </select>
      </td>
      <td className="px-4 py-2.5">
        <div className="flex items-center justify-end gap-2 flex-wrap">
          {status.connected && (
            <button onClick={handleDisconnect} disabled={busy} className="btn btn-ghost btn-sm text-xs text-danger-600 whitespace-nowrap">
              {t('automationSources.youtube.disconnect')}
            </button>
          )}
          <button onClick={handleDelete} disabled={busy} title={t('ipWhitelist.remove')} className="text-gray-400 hover:text-danger-600 transition">
            <FiTrash2 size={14} />
          </button>
        </div>
      </td>
    </tr>
  );
};

// ─── Table 3: Default destination mappings ─────────────────────────────────────
// "This WhatsApp group/contact always publishes to this destination by default."
// Configured here once; only pre-fills a fresh Pipeline item's destination at
// capture time (see WhatsAppConnectionService) — always changeable per item on
// the Automation page before publishing.

const MAPPINGS_PAGE_SIZE = 25;

const DefaultMappingsPanel = ({ t, whatsappSource, outputSources }) => {
  const [mappingByKey, setMappingByKey] = useState({}); // `${matchType}:${matchValue}` -> destinationKey
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [savingKey, setSavingKey] = useState('');

  const monitored = whatsappSource?.config?.monitored || { contacts: [], groups: [] };

  const entries = useMemo(() => [
    ...(monitored.groups || []).map((g) => ({ matchType: 'group', matchValue: g.name, matchLabel: g.label || g.name })),
    ...(monitored.contacts || []).map((c) => ({ matchType: 'contact', matchValue: c.number, matchLabel: c.label || `+${c.number}` })),
    // eslint-disable-next-line
  ], [whatsappSource?._id, JSON.stringify(monitored)]);

  const destinationOptions = useMemo(
    () => outputSources.map((s) => ({ key: s.key, label: s.name })),
    [outputSources]
  );

  const loadMappings = useCallback(async () => {
    setLoading(true);
    try {
      // limit generously so the whole allow-list's defaults come back in one
      // request — bounded by "however many groups/contacts are monitored,"
      // which is exactly the same scale this table itself renders.
      const data = await mappingService.list({ sourceKey: 'whatsapp', limit: 1000 });
      const map = {};
      (data?.mappings || []).forEach((m) => { map[`${m.matchType}:${m.matchValue}`] = m.destinationKey; });
      setMappingByKey(map);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMappings(); }, [loadMappings]);

  const filteredEntries = search.trim()
    ? entries.filter((e) => e.matchLabel.toLowerCase().includes(search.trim().toLowerCase()))
    : entries;
  const pageCount = Math.max(1, Math.ceil(filteredEntries.length / MAPPINGS_PAGE_SIZE));
  const pageEntries = filteredEntries.slice((page - 1) * MAPPINGS_PAGE_SIZE, page * MAPPINGS_PAGE_SIZE);

  const handleSetDestination = async (entry, destinationKey) => {
    const mapKey = `${entry.matchType}:${entry.matchValue}`;
    setSavingKey(mapKey);
    try {
      await mappingService.upsert({
        sourceKey: 'whatsapp',
        matchType: entry.matchType,
        matchValue: entry.matchValue,
        matchLabel: entry.matchLabel,
        destinationKey,
      });
      setMappingByKey((prev) => ({ ...prev, [mapKey]: destinationKey }));
      toast.success(t('automationSources.mappings.saved'));
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setSavingKey('');
    }
  };

  return (
    // No overflow-hidden here (unlike other card sections) — each row's
    // SearchableSelect portals its options panel to <body> now, so this no
    // longer strictly matters for clipping, but the header still gets its
    // own rounded-t-xl since there's no wrapping overflow-hidden to imply it.
    <div className="card p-0">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 rounded-t-xl flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-semibold text-gray-800 flex items-center gap-2">
            <StepBadge n={3} />
            {t('automationSources.mappings.title')}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">{t('automationSources.mappings.subtitle')}</p>
        </div>
        {entries.length > 0 && (
          <div className="relative">
            <FiSearch size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder={t('automationSources.mappings.searchPlaceholder')}
              className="input text-sm pl-8 w-56"
            />
          </div>
        )}
      </div>

      {entries.length === 0 ? (
        <p className="px-4 py-6 text-sm text-gray-400 text-center">{t('automationSources.mappings.emptyNoInputs')}</p>
      ) : destinationOptions.length === 0 ? (
        <p className="px-4 py-6 text-sm text-gray-400 text-center">{t('automationSources.mappings.emptyNoOutputs')}</p>
      ) : loading ? (
        <p className="px-4 py-6 text-sm text-gray-400 text-center">{t('settings.loadingPrompts')}</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-gray-100">
                  <th className="px-4 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('automationSources.inputs.title')}</th>
                  <th className="px-4 py-2" />
                  <th className="px-4 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('automationSources.outputs.title')}</th>
                </tr>
              </thead>
              <tbody>
                {pageEntries.map((entry) => {
                  const mapKey = `${entry.matchType}:${entry.matchValue}`;
                  return (
                    <tr key={mapKey} className="border-b border-gray-50 last:border-0">
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide mr-2">
                          {entry.matchType === 'group' ? <FiUsers size={11} /> : <FiUser size={11} />}
                        </span>
                        <span className="text-gray-800">{entry.matchLabel}</span>
                      </td>
                      <td className="px-2 py-2.5 text-gray-300">
                        <FiArrowRight size={14} />
                      </td>
                      <td className="px-4 py-2.5">
                        <SearchableSelect
                          value={mappingByKey[mapKey] || ''}
                          onChange={(key) => handleSetDestination(entry, key)}
                          options={destinationOptions}
                          placeholder={t('automationSources.mappings.destinationPlaceholder')}
                          disabled={savingKey === mapKey}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div className="px-4 py-2.5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn btn-ghost btn-sm text-xs disabled:opacity-40"
                aria-label="Previous page"
              >
                ‹
              </button>
              <span>{page} / {pageCount}</span>
              <button
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                disabled={page >= pageCount}
                className="btn btn-ghost btn-sm text-xs disabled:opacity-40"
                aria-label="Next page"
              >
                ›
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
